import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { PageHeader, Stat, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import {
  ClipboardText, ShieldCheck, Buildings, UsersThree, Package,
  CheckCircle, XCircle, Funnel, MagnifyingGlass, Warning, Check, X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ExportMenu from "@/components/app/ExportMenu";
import SalaryBatchesPanel from "@/components/app/SalaryBatchesPanel";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

const MODULE_ICON = {
  HR: UsersThree,
  Assets: Package,
  Appointments: ClipboardText,
  Tasks: ClipboardText,
  Compliance: ShieldCheck,
  Projects: Buildings,
};

const STATUS_LABEL = {
  pending: "Pending",
  awaiting_finance: "Awaiting Finance",
  awaiting_admin: "Awaiting Admin",
  approved: "Approved",
  rejected: "Rejected",
  active: "Active",
};

const PRIORITY_COLOR = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high: "bg-amber-100 text-amber-700 border-amber-300",
  medium: "bg-[#EFF4FC] text-[#1E5CBA] border-[#A8C2EC]",
  low: "bg-slate-100 text-slate-600 border-slate-300",
};

function DecisionDialog({ open, onOpenChange, item, decision, onConfirm, busy }) {
  const [comment, setComment] = useState("");
  useEffect(() => { if (open) setComment(""); }, [open]);
  if (!item) return null;
  const isApprove = decision === "approve";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isApprove ? "Approve" : "Reject"} · {item.title}
          </DialogTitle>
          <DialogDescription>
            {item.module} · {item.stage}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            {item.subtitle || "—"}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">
              {isApprove ? "Approval note (optional)" : "Reason for rejection (optional)"}
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={isApprove ? "All checks passed..." : "Why is this being rejected..."}
              rows={3}
              data-testid="decision-comment"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => onConfirm(comment)}
            disabled={busy}
            className={isApprove ? "btn-primary-blue" : ""}
            style={!isApprove ? { background: "#dc2626", color: "#fff" } : undefined}
            data-testid="decision-confirm"
          >
            {busy ? "Working…" : isApprove ? "Approve" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Approvals() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ module: "", kind: "", priority: "", status: "", search: "" });
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState("approve");
  const [selectedItem, setSelectedItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDecision, setBulkDecision] = useState("approve");
  const [bulkComment, setBulkComment] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const inspector = useRecordInspector();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sRes, iRes] = await Promise.all([
        api.get("/approvals/summary"),
        api.get("/approvals", { params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) }),
      ]);
      setSummary(sRes.data);
      setItems(iRes.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filters]);

  const kinds = useMemo(() => {
    const set = new Set((items || []).map((i) => i.kind));
    return Array.from(set);
  }, [items]);

  const openDecision = (item, d) => {
    setSelectedItem(item);
    setDecision(d);
    setDecisionOpen(true);
  };

  // Pending items only (eligible for bulk action)
  const pendingItems = items.filter((it) =>
    !["approved", "rejected", "active"].includes(it.status));

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllVisible = () => setSelectedIds((s) =>
    s.size === pendingItems.length
      ? new Set()
      : new Set(pendingItems.map((it) => it.source_id))
  );

  const selectedPending = pendingItems.filter((it) => selectedIds.has(it.source_id));

  const openBulk = (d) => {
    if (selectedPending.length === 0) {
      toast.error("Select at least one pending item");
      return;
    }
    setBulkDecision(d);
    setBulkComment("");
    setBulkResult(null);
    setBulkOpen(true);
  };

  const submitBulk = async () => {
    setBusy(true);
    try {
      // Salary requests need a per-record call; route them through their
      // own workflow endpoint, then bundle the rest into /approvals/bulk-decide.
      const salaryItems = selectedPending.filter((it) => it.kind === "salary_request");
      const otherItems = selectedPending.filter((it) => it.kind !== "salary_request");

      const result = { succeeded: [], failed: [],
                        succeeded_count: 0, failed_count: 0,
                        total: selectedPending.length, decision: bulkDecision };

      for (const it of salaryItems) {
        try {
          const target = bulkDecision === "approve" ? "approved"
            : bulkDecision === "reject" ? "rejected" : "draft";
          await api.post(`/hr/salary-requests/${it.source_id}/transition`,
            { status: target, comment: bulkComment || "" });
          result.succeeded.push({ source_collection: "hr_salary_requests",
                                   source_id: it.source_id, new_status: target });
          result.succeeded_count++;
        } catch (err) {
          result.failed.push({ source_collection: "hr_salary_requests",
                               source_id: it.source_id,
                               reason: formatApiError(err) });
          result.failed_count++;
        }
      }

      if (otherItems.length > 0) {
        const { data } = await api.post("/approvals/bulk-decide", {
          decision: bulkDecision,
          comment: bulkComment,
          items: otherItems.map((it) => ({
            source_collection: it.source_collection,
            source_id: it.source_id,
          })),
        });
        result.succeeded.push(...(data.succeeded || []));
        result.failed.push(...(data.failed || []));
        result.succeeded_count += data.succeeded_count || 0;
        result.failed_count += data.failed_count || 0;
      }
      setBulkResult(result);
      toast.success(`${result.succeeded_count} succeeded · ${result.failed_count} failed`);
      setSelectedIds(new Set());
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmDecision = async (comment) => {
    if (!selectedItem) return;
    setBusy(true);
    try {
      if (selectedItem.kind === "salary_request") {
        // Use existing HR workflow transition
        const target = decision === "approve" ? "approved" : "rejected";
        await api.post(
          `/hr/salary-requests/${selectedItem.source_id}/transition`,
          { status: target, comment: comment || "" },
        );
      } else {
        await api.post(
          `/approvals/${selectedItem.source_collection}/${selectedItem.source_id}/decide`,
          null,
          { params: { decision, comment: comment || undefined } },
        );
      }
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      setDecisionOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="approvals-page">
      <PageHeader
        eyebrow="Central hub"
        title="Approvals"
        description="Every workflow awaiting a decision across HR, Assets, Tasks, and Appointments — in one place."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExportMenu module="approvals" />
            <Button variant="outline" onClick={loadAll} data-testid="approvals-refresh-btn">
              Refresh
            </Button>
          </div>
        }
      />

      <div className="mx-auto max-w-[1400px] space-y-6 px-6 py-6 md:px-8">
        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <Stat
            label="Total pending"
            value={summary?.total_pending ?? "—"}
            sub="Across all modules"
            testId="approvals-stat-total"
            accent
          />
          <Stat
            label="Awaiting Finance"
            value={summary?.awaiting_finance ?? "—"}
            sub="Finance clearance step"
            testId="approvals-stat-finance"
          />
          <Stat
            label="Awaiting Admin"
            value={summary?.awaiting_admin ?? "—"}
            sub="Final sign-off step"
            testId="approvals-stat-admin"
          />
          <Stat
            label="Approved today"
            value={summary?.approved_today ?? 0}
            sub="Payroll & payouts"
            testId="approvals-stat-approved-today"
          />
          <Stat
            label="Rejected today"
            value={summary?.rejected_today ?? 0}
            sub="Critical: Active flagged items"
            testId="approvals-stat-rejected-today"
          />
        </div>

        {/* HR Payroll batches — final-approval queue for CEO */}
        <section className="dc-card p-4 md:p-5" data-testid="approvals-hr-payroll">
          <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="overline">HR Payroll · CEO final approval</div>
              <h2 className="font-display text-lg font-semibold text-slate-900">Bulk salary batches awaiting your decision</h2>
              <p className="text-xs text-slate-500">
                Cleared by Finance and forwarded for CEO sign-off. Approve, partially approve, reject, or send back for correction.
              </p>
            </div>
          </div>
          <SalaryBatchesPanel mode="approvals" />
        </section>

        {/* By-module breakdown */}
        {summary?.by_module && Object.keys(summary.by_module).length > 0 && (
          <div className="dc-card p-4" data-testid="approvals-by-module">
            <div className="overline mb-3">By module</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.by_module).map(([mod, count]) => {
                const Icon = MODULE_ICON[mod] || ClipboardText;
                return (
                  <button
                    key={mod}
                    onClick={() => setFilters((f) => ({ ...f, module: f.module === mod ? "" : mod }))}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
                      filters.module === mod
                        ? "border-[#1E5CBA] bg-[#EFF4FC] text-[#1E5CBA]"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                    data-testid={`approvals-module-chip-${mod}`}
                  >
                    <Icon size={14} />
                    <span className="font-medium">{mod}</span>
                    <span className="font-mono-alt text-slate-500">· {count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="dc-card flex flex-wrap items-end gap-3 p-4" data-testid="approvals-filters">
          <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-slate-200 bg-white px-3">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search title or subtitle…"
              className="border-0 shadow-none focus-visible:ring-0"
              data-testid="approvals-search-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Status</label>
            <Select value={filters.status || "__all__"} onValueChange={(v) => setFilters({ ...filters, status: v === "__all__" ? "" : v })}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="approvals-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="awaiting_finance">Awaiting Finance</SelectItem>
                <SelectItem value="awaiting_admin">Awaiting Admin</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Type</label>
            <Select value={filters.kind || "__all__"} onValueChange={(v) => setFilters({ ...filters, kind: v === "__all__" ? "" : v })}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="approvals-filter-kind"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All types</SelectItem>
                {kinds.map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Priority</label>
            <Select value={filters.priority || "__all__"} onValueChange={(v) => setFilters({ ...filters, priority: v === "__all__" ? "" : v })}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="approvals-filter-priority"><SelectValue placeholder="Any priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any priority</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(filters.module || filters.status || filters.kind || filters.priority || filters.search) && (
            <Button
              variant="outline"
              onClick={() => setFilters({ module: "", kind: "", priority: "", status: "", search: "" })}
              data-testid="approvals-clear-filters"
            >
              Clear
            </Button>
          )}
        </div>

        {/* Items table */}
        <div className="dc-card overflow-hidden" data-testid="approvals-table-card">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <div className="flex items-center gap-2">
              <Funnel size={14} className="text-slate-500" />
              <div className="font-display text-sm font-semibold text-slate-900">
                {loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"}`}
              </div>
            </div>
          </div>
          {items.length === 0 && !loading ? (
            <EmptyState
              icon={CheckCircle}
              title="Nothing pending"
              description="Every workflow is up to date. New requests will appear here automatically."
            />
          ) : (
            <>
            {/* Bulk action bar */}
            {selectedPending.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2 text-sm">
                <span className="font-semibold text-blue-900" data-testid="bulk-selection-count">
                  {selectedPending.length} selected
                </span>
                <span className="text-blue-700 mr-auto">
                  Apply action to all selected items.
                </span>
                <button onClick={() => openBulk("approve")} disabled={busy}
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                  data-testid="bulk-approve-btn">
                  ✓ Bulk approve
                </button>
                <button onClick={() => openBulk("reject")} disabled={busy}
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                  data-testid="bulk-reject-btn">
                  ✗ Bulk reject
                </button>
                <button onClick={() => openBulk("return")} disabled={busy}
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                  data-testid="bulk-return-btn">
                  ↺ Return for correction
                </button>
                <button onClick={() => setSelectedIds(new Set())}
                  className="rounded-md border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">
                  Clear
                </button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="w-8 px-3 py-2">
                      <input type="checkbox"
                        checked={pendingItems.length > 0 && selectedIds.size === pendingItems.length}
                        onChange={toggleAllVisible}
                        data-testid="bulk-select-all" />
                    </th>
                    <th className="px-4 py-2 text-left">Request</th>
                    <th className="px-4 py-2 text-left">Module</th>
                    <th className="px-4 py-2 text-left">Stage</th>
                    <th className="px-4 py-2 text-left">Priority</th>
                    <th className="px-4 py-2 text-right">Amount (USD)</th>
                    <th className="px-4 py-2 text-left">Age</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {items.map((it) => {
                    const Icon = MODULE_ICON[it.module] || ClipboardText;
                    return (
                      <tr
                        key={it.id}
                        className="hover:bg-slate-50/70 cursor-pointer"
                        onClick={() => inspector.open(it, {
                          title: it.title || "Approval request",
                          subtitle: `${it.module} · ${it.kind?.replace(/_/g, " ") || ""}`,
                        })}
                        data-testid={`approval-row-${it.source_id}`}
                      >
                        <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                          {["approved","rejected","active"].includes(it.status) ? (
                            <span className="text-slate-300 text-xs">—</span>
                          ) : (
                            <input type="checkbox"
                              checked={selectedIds.has(it.source_id)}
                              onChange={() => toggleSelect(it.source_id)}
                              data-testid={`bulk-row-cb-${it.source_id}`} />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{it.title}</div>
                          {it.subtitle && (
                            <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">{it.subtitle}</div>
                          )}
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                            {it.kind.replace(/_/g, " ")} · by {it.requested_by || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="inline-flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-0.5 text-xs text-slate-700">
                            <Icon size={12} />{it.module}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <StatusBadge status={it.status} />
                          <div className="mt-1 text-xs text-slate-500">{it.stage}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-wider ${PRIORITY_COLOR[it.priority] || PRIORITY_COLOR.medium}`}>
                            {it.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right align-top tabular-nums text-slate-700">
                          {it.amount != null ? formatCurrency(it.amount) : "—"}
                        </td>
                        <td className="px-4 py-3 align-top text-xs text-slate-500">
                          {it.age_days != null ? `${it.age_days}d` : "—"}
                          <div className="text-[10px] text-slate-400">{formatDateTime(it.created_at)}</div>
                        </td>
                        <td className="px-4 py-3 text-right align-top" onClick={(e) => e.stopPropagation()}>
                          {["approved", "rejected", "active"].includes(it.status) ? (
                            <div className="inline-flex items-center gap-1 text-xs text-slate-400">
                              <Check size={12} /> decided
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openDecision(it, "approve")}
                                className="flex h-7 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                                data-testid={`approve-btn-${it.source_id}`}
                              >
                                <Check size={12} /> Approve
                              </button>
                              <button
                                onClick={() => openDecision(it, "reject")}
                                className="flex h-7 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 text-xs font-medium text-red-700 hover:bg-red-100"
                                data-testid={`reject-btn-${it.source_id}`}
                              >
                                <X size={12} /> Reject
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      <DecisionDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        item={selectedItem}
        decision={decision}
        onConfirm={confirmDecision}
        busy={busy}
      />

      {/* Bulk decision dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Bulk {bulkDecision} · {selectedPending.length} item{selectedPending.length === 1 ? "" : "s"}
            </DialogTitle>
            <DialogDescription>
              This action will run against each selected item. Items that fail
              gating rules (e.g. multi-step salary requests) will be reported
              back so you can handle them individually.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <>
              <div className="max-h-44 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 text-xs">
                {selectedPending.slice(0, 25).map((it) => (
                  <div key={it.source_id} className="flex items-center gap-1.5 py-0.5">
                    <span className="font-mono-alt text-[10px] uppercase rounded bg-white px-1.5 text-slate-600">{it.kind.replace(/_/g, " ")}</span>
                    <span className="truncate">{it.title}</span>
                  </div>
                ))}
                {selectedPending.length > 25 && <div className="pt-1 text-slate-500">…and {selectedPending.length - 25} more</div>}
              </div>
              <div className="mt-3">
                <Label htmlFor="bulk-comment">Comment for this bulk action</Label>
                <Textarea id="bulk-comment" rows={3} value={bulkComment}
                  onChange={(e) => setBulkComment(e.target.value)}
                  placeholder={bulkDecision === "return"
                    ? "What needs to be corrected before resubmission…"
                    : bulkDecision === "reject"
                    ? "Reason for rejection…"
                    : "Optional context, e.g. 'May payroll cycle cleared.'"}
                  data-testid="bulk-comment-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button onClick={submitBulk} disabled={busy} className="btn-primary-blue"
                        data-testid="bulk-confirm">
                  {busy ? "Processing…" : `Confirm bulk ${bulkDecision}`}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  ✓ {bulkResult.succeeded_count} of {bulkResult.total} succeeded
                </div>
                {bulkResult.failed_count > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
                    <div className="font-semibold text-red-800 mb-1.5">
                      ⚠️ {bulkResult.failed_count} item{bulkResult.failed_count === 1 ? "" : "s"} need manual attention
                    </div>
                    <ul className="space-y-1 text-xs text-red-900">
                      {bulkResult.failed.map((f, i) => (
                        <li key={i}>
                          <span className="font-mono-alt uppercase mr-1">{f.source_collection}</span>
                          {f.source_id?.slice(0, 8)} → <em>{f.reason}</em>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setBulkOpen(false)} className="btn-primary-blue">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <RecordInspector {...inspector.props} />
    </div>
  );
}
